<template>
  <div class="container-fluid py-4">
    <!-- Header -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card border-0 shadow-sm" style="background: linear-gradient(135deg, #6f42c1, #8b5cf6);">
          <div class="card-body text-white">
            <div>
              <h4 class="mb-1">
                <i class="fas fa-robot me-2"></i>
                {{ $t('scraperManagement.title') }}
              </h4>
              <p class="mb-0 opacity-75">{{ $t('scraperManagement.description') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Current Status -->
    <div class="row mb-4">
      <div class="col-md-4">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body">
            <div class="d-flex align-items-center">
              <div class="flex-shrink-0">
                <i class="fas fa-circle fa-2x" :class="statusIconClass"></i>
              </div>
              <div class="flex-grow-1 ms-3">
                <h6 class="card-title mb-1">{{ $t('scraperManagement.currentStatus') }}</h6>
                <p class="card-text mb-0">
                  <span class="badge" :class="statusBadgeClass">{{ currentStatus }}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="col-md-4">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body">
            <div class="d-flex align-items-center">
              <div class="flex-shrink-0">
                <i class="fas fa-clock fa-2x text-info"></i>
              </div>
              <div class="flex-grow-1 ms-3">
                <h6 class="card-title mb-1">{{ $t('scraperManagement.lastRun') }}</h6>
                <p class="card-text mb-0">{{ lastRunTime }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="col-md-4">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body">
            <div class="d-flex align-items-center">
              <div class="flex-shrink-0">
                <i class="fas fa-database fa-2x text-success"></i>
              </div>
              <div class="flex-grow-1 ms-3">
                <h6 class="card-title mb-1">{{ $t('scraperManagement.productsScraped') }}</h6>
                <p class="card-text mb-0">{{ totalProductsScraped }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Scraping Logs -->
    <div class="row">
      <div class="col-12">
        <div class="card border-0 shadow-sm">
          <div class="card-header bg-white border-bottom">
            <div class="d-flex align-items-center justify-content-between">
              <h5 class="mb-0">
                <i class="fas fa-list me-2"></i>
                {{ $t('scraperManagement.scrapingHistory') }}
              </h5>
              <button class="btn btn-outline-primary btn-sm" @click="refreshLogs">
                <i class="fas fa-sync-alt me-1"></i>
                {{ $t('scraperManagement.refresh') }}
              </button>
            </div>
          </div>
          <div class="card-body">
            <div v-if="loading" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">{{ $t('scraperManagement.loading') }}</span>
              </div>
            </div>
            
            <div v-else-if="logs.length === 0" class="text-center py-4 text-muted">
              <i class="fas fa-inbox fa-3x mb-3"></i>
              <p>{{ $t('scraperManagement.noLogsFound') }}</p>
            </div>
            
            <div v-else class="table-responsive">
              <table class="table table-hover">
                <thead class="table-light">
                  <tr>
                    <th>{{ $t('scraperManagement.table.source') }}</th>
                    <th>{{ $t('scraperManagement.table.status') }}</th>
                    <th>{{ $t('scraperManagement.table.started') }}</th>
                    <th>{{ $t('scraperManagement.table.duration') }}</th>
                    <th>{{ $t('scraperManagement.table.products') }}</th>
                    <th>{{ $t('scraperManagement.table.actions') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="log in logs" :key="log.id">
                    <td>{{ log.source }}</td>
                    <td>
                      <span class="badge" :class="getStatusBadgeClass(log.status)">
                        {{ log.status }}
                      </span>
                    </td>
                    <td>{{ formatDateTime(log.started_at) }}</td>
                    <td>{{ formatDuration(log.duration) }}</td>
                    <td>{{ log.products_scraped || 0 }}</td>
                    <td>
                      <button 
                        class="btn btn-sm btn-outline-info me-1"
                        @click="viewLogDetails(log)"
                        title="{{ $t('scraperManagement.viewDetails') }}"
                      >
                        <i class="fas fa-eye"></i>
                      </button>
                      <button 
                        v-if="log.status !== 'running'"
                        class="btn btn-sm btn-outline-danger"
                        @click="deleteLog(log.id)"
                        title="{{ $t('scraperManagement.deleteLog') }}"
                      >
                        <i class="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Trigger Scraping Modal -->
    <div class="modal fade" :class="{ show: showTriggerModal }" :style="{ display: showTriggerModal ? 'block' : 'none' }" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-play me-2"></i>
              {{ $t('scraperManagement.triggerScraping') }}
            </h5>
            <button type="button" class="btn-close" @click="showTriggerModal = false"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="triggerScraping">
              <div class="mb-3">
                <label for="scraperType" class="form-label">{{ $t('scraperManagement.platform') }}</label>
                <select 
                  id="scraperType" 
                  v-model="triggerForm.scraper_type" 
                  class="form-select"
                  required
                >
                  <option value="amazon">Amazon</option>
                  <option value="bestbuy">BestBuy</option>
                </select>
              </div>
              
              <div class="mb-3">
                <label for="keywords" class="form-label">{{ $t('scraperManagement.keywords') }}</label>
                <textarea 
                  id="keywords" 
                  v-model="keywordsText" 
                  class="form-control" 
                  rows="4"
                  :placeholder="`${$t('scraperManagement.keywordsPlaceholder')}\n${$t('scraperManagement.keywordsExample')}\nADATA SSD\nXPG DDR5\nCorsair Memory`"
                  required
                ></textarea>
                <div class="form-text">{{ $t('scraperManagement.keywordsHint') }}</div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showTriggerModal = false">
              {{ $t('scraperManagement.cancel') }}
            </button>
            <button 
              type="button" 
              class="btn btn-primary"
              @click="triggerScraping"
              :disabled="triggerLoading"
            >
              <span v-if="triggerLoading" class="spinner-border spinner-border-sm me-2"></span>
              {{ triggerLoading ? $t('scraperManagement.starting') : $t('scraperManagement.startScraping') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Log Details Modal -->
    <div class="modal fade" :class="{ show: showDetailsModal }" :style="{ display: showDetailsModal ? 'block' : 'none' }" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-info-circle me-2"></i>
              {{ $t('scraperManagement.logDetails') }}
            </h5>
            <button type="button" class="btn-close" @click="showDetailsModal = false"></button>
          </div>
          <div class="modal-body" v-if="selectedLog">
            <div class="row">
              <div class="col-md-6">
                <strong>{{ $t('scraperManagement.table.source') }}:</strong> {{ selectedLog.source }}
              </div>
              <div class="col-md-6">
                <strong>{{ $t('scraperManagement.table.status') }}:</strong> 
                <span class="badge" :class="getStatusBadgeClass(selectedLog.status)">
                  {{ selectedLog.status }}
                </span>
              </div>
            </div>
            <hr>
            <div class="row">
              <div class="col-md-6">
                <strong>{{ $t('scraperManagement.table.started') }}:</strong> {{ formatDateTime(selectedLog.started_at) }}
              </div>
              <div class="col-md-6">
                <strong>{{ $t('scraperManagement.completed') }}:</strong> {{ selectedLog.completed_at ? formatDateTime(selectedLog.completed_at) : $t('scraperManagement.na') }}
              </div>
            </div>
            <hr>
            <div class="row">
              <div class="col-md-6">
                <strong>{{ $t('scraperManagement.table.duration') }}:</strong> {{ formatDuration(selectedLog.duration) }}
              </div>
              <div class="col-md-6">
                <strong>{{ $t('scraperManagement.productsScraped') }}:</strong> {{ selectedLog.products_scraped || 0 }}
              </div>
            </div>
            <div v-if="selectedLog.errors" class="mt-3">
              <strong>{{ $t('scraperManagement.errors') }}:</strong>
              <pre class="bg-light p-3 mt-2 rounded">{{ selectedLog.errors }}</pre>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showDetailsModal = false">
              {{ $t('scraperManagement.close') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Backdrop -->
    <div v-if="showTriggerModal || showDetailsModal" class="modal-backdrop fade show"></div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import apiClient from '../api';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

// Reactive data
const logs = ref([]);
const loading = ref(false);
const showTriggerModal = ref(false);
const showDetailsModal = ref(false);
const selectedLog = ref(null);
const triggerLoading = ref(false);

const triggerForm = ref({
  scraper_type: 'amazon',
  keywords: []
});

const keywordsText = ref('ADATA SSD\nXPG DDR5\nCorsair Memory\nKingston RAM');

// Computed properties
const currentStatus = computed(() => {
  const runningLog = logs.value.find(log => log.status === 'running');
  if (runningLog) return t('scraperManagement.running');
  
  const queuedLog = logs.value.find(log => log.status === 'queued');
  if (queuedLog) return t('scraperManagement.queued');
  
  return t('scraperManagement.idle');
});

const statusIconClass = computed(() => {
  switch (currentStatus.value) {
    case 'Running': return 'text-warning';
    case 'Queued': return 'text-info';
    default: return 'text-success';
  }
});

const statusBadgeClass = computed(() => {
  switch (currentStatus.value) {
    case 'Running': return 'bg-warning';
    case 'Queued': return 'bg-info';
    default: return 'bg-success';
  }
});

const lastRunTime = computed(() => {
  if (logs.value.length === 0) return t('scraperManagement.never');
  const lastLog = logs.value[0];
  return formatDateTime(lastLog.started_at);
});

const totalProductsScraped = computed(() => {
  return logs.value.reduce((total, log) => total + (log.products_scraped || 0), 0);
});

// Methods
const fetchLogs = async () => {
  loading.value = true;
  try {
    const response = await apiClient.get('/scraper/status?limit=20');
    logs.value = response.data;
  } catch (error) {
    console.error('Failed to fetch scraping logs:', error);
  } finally {
    loading.value = false;
  }
};

const refreshLogs = () => {
  fetchLogs();
};

const triggerScraping = async () => {
  triggerLoading.value = true;
  try {
    const keywords = keywordsText.value
      .split('\n')
      .map(k => k.trim())
      .filter(k => k.length > 0);
    
    const response = await apiClient.post('/scraper/trigger', {
      keywords,
      scraper_type: triggerForm.value.scraper_type
    });
    
    showTriggerModal.value = false;
    
    // Show success message
    alert(`${t('scraperManagement.taskQueued')} ${response.data.scraping_log_id}`);
    
    // Refresh logs
    await fetchLogs();
  } catch (error) {
    console.error('Failed to trigger scraping:', error);
    alert(`${t('scraperManagement.triggerFailed')} ${error.response?.data?.detail || error.message}`);
  } finally {
    triggerLoading.value = false;
  }
};

const viewLogDetails = (log) => {
  selectedLog.value = log;
  showDetailsModal.value = true;
};

const deleteLog = async (logId) => {
  if (!confirm(t('scraperManagement.confirmDelete'))) return;
  
  try {
    await apiClient.delete(`/scraper/logs/${logId}`);
    await fetchLogs();
  } catch (error) {
    console.error('Failed to delete log:', error);
    alert(`${t('scraperManagement.deleteFailed')} ${error.response?.data?.detail || error.message}`);
  }
};

const getStatusBadgeClass = (status) => {
  switch (status) {
    case 'success': return 'bg-success';
    case 'running': return 'bg-warning';
    case 'queued': return 'bg-info';
    case 'failed': return 'bg-danger';
    case 'partial': return 'bg-warning';
    case 'stopped': return 'bg-secondary';
    default: return 'bg-secondary';
  }
};

const formatDateTime = (dateString) => {
  if (!dateString) return t('scraperManagement.na');
  return new Date(dateString).toLocaleString();
};

const formatDuration = (duration) => {
  if (!duration) return t('scraperManagement.na');
  if (duration < 60) return `${duration.toFixed(1)}s`;
  if (duration < 3600) return `${(duration / 60).toFixed(1)}m`;
  return `${(duration / 3600).toFixed(1)}h`;
};

// Lifecycle
onMounted(() => {
  fetchLogs();
  
  // Auto-refresh every 30 seconds
  setInterval(fetchLogs, 30000);
});
</script>

<style scoped>
.modal.show {
  display: block !important;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1040;
  width: 100vw;
  height: 100vh;
  background-color: #000;
  opacity: 0.5;
}

.table th {
  border-top: none;
  font-weight: 600;
  color: #495057;
}

.badge {
  font-size: 0.75rem;
}

pre {
  font-size: 0.875rem;
  max-height: 200px;
  overflow-y: auto;
}
</style>